import { Button, PageModal } from '@dev-spendesk/grapes';

import { Stepper } from 'src/core/common/components/Stepper';
import { FieldValuesMapping } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/FieldValuesMapping/FieldValuesMapping';
import { SyncSettingsRadioList } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/SyncSettings/SyncSettingsRadioList';
import {
  type AnalyticalFieldMappingFieldType,
  type AnalyticalFieldMappingV2,
  type InternalValue,
} from 'src/core/modules/bookkeep';

import { type AnalyticalFieldExportSettings } from '../../apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from '../../apis/useSetSyncSettings';
import { type SaveAllChanges } from '../../hooks/useAnalyticalFieldMapping';
import { useAnalyticalFieldMappingSetupModal } from '../../hooks/useAnalyticalFieldMappingSetupModal';
import { ExportSettings } from '../ExportSettings/ExportSettings';

type Props<TField extends InternalValue, TValue extends InternalValue> = {
  internalField: TField;
  fieldType: AnalyticalFieldMappingFieldType;
  isOpen: boolean;
  isExportConfigEnabled: boolean;
  isSplittable: boolean;
  fieldExportSetting?: AnalyticalFieldExportSettings | undefined;
  isIntegrationNameLoading: boolean;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  values: TValue[];
  syncMode: SyncMode | undefined;
  onClose: () => void;
  onSaveChanges: SaveAllChanges;
  reset: () => void;
  onDeleteValue?: (value: TValue) => void;
};

export const AnalyticalFieldMappingSetupModal = <
  TField extends InternalValue,
  TValue extends InternalValue,
>({
  isOpen,
  isExportConfigEnabled,
  isIntegrationNameLoading,
  isSplittable,
  analyticalFieldMappings,
  fieldType,
  internalField,
  values,
  fieldExportSetting,
  syncMode,
  onSaveChanges,
  onClose,
  reset,
  onDeleteValue,
}: Props<TField, TValue>) => {
  const {
    stepTitles,
    mode,
    selectedSyncMode,
    fieldMapping,
    backButton,
    moveBack,
    nextButton,
    moveNext,
    setFieldMapping,
    setExportConfig,
    isSyncSettingsMode,
    isMappingMode,
    isExportSettingsMode,
    handleSyncModeChanged,
    fieldMappingValues,
    setFieldMappingValues,
    errors,
    setErrors,
  } = useAnalyticalFieldMappingSetupModal({
    isOpen,
    isExportConfigEnabled,
    isIntegrationNameLoading,
    analyticalFieldMappings,
    fieldType,
    internalField,
    values,
    fieldExportSetting,
    syncMode,
    onSaveChanges,
    onClose,
    reset,
  });

  return (
    <>
      <PageModal
        isOpen={isOpen}
        onClose={onClose}
        className="fixed"
        title={
          <Stepper
            className="[&_header]:-z-10 [&_header]:border-none"
            stepsTitles={stepTitles}
            currentStepIndex={mode}
          >
            <div />
          </Stepper>
        }
      >
        <div className="m-auto h-full w-full max-w-md pt-32">
          {isSyncSettingsMode() && (
            <SyncSettingsRadioList
              fieldName={internalField.name}
              value={selectedSyncMode}
              onChange={handleSyncModeChanged}
              currentlyMappedTo={fieldMapping?.externalName}
              allowedSyncSettings={
                fieldType === 'costCenter' ? ['pullAndMap', 'none'] : undefined
              }
            />
          )}
          {isMappingMode() && (
            <FieldValuesMapping
              fieldType={fieldType}
              isIntegrationNameLoading={isIntegrationNameLoading}
              internalField={internalField}
              syncMode={selectedSyncMode}
              analyticalFieldMappings={analyticalFieldMappings}
              internalValues={values}
              fieldMapping={fieldMapping}
              setFieldMapping={setFieldMapping}
              fieldMappingValues={fieldMappingValues}
              setFieldMappingValues={setFieldMappingValues}
              hasError={errors.valueMapping}
              onDelete={onDeleteValue}
              hideBorder
              showTitle
            />
          )}
          {isExportSettingsMode() && (
            <ExportSettings
              isSplittable={isSplittable}
              fieldType={fieldType}
              value={fieldExportSetting}
              fieldName={internalField.name}
              syncMode={selectedSyncMode}
              externalField={
                fieldMapping
                  ? {
                      id: fieldMapping.externalId,
                      name: fieldMapping.externalName,
                    }
                  : undefined
              }
              variant="primary"
              onChange={(settings) => {
                if (settings.sendOnHeader || settings.sendOnLine) {
                  setErrors({
                    ...errors,
                    exportSettings: false,
                  });
                }

                if (fieldMapping) {
                  setExportConfig({
                    ...settings,
                    externalId: fieldMapping.externalId,
                  });
                }
              }}
              hasError={errors.exportSettings}
            />
          )}
        </div>
        <div className="border-border-default bg-background-primary-default absolute inset-x-0 bottom-0 w-full border-t-1 py-16">
          <div className="m-auto w-full max-w-md">
            <div className="flex justify-between">
              <div>
                {backButton && (
                  <Button
                    variant="secondaryNeutral"
                    text={backButton.text}
                    iconName={backButton.icon}
                    iconPosition="left"
                    onClick={moveBack}
                  />
                )}
              </div>
              <div>
                <Button
                  variant="primaryBrand"
                  text={nextButton.text}
                  iconName={nextButton.icon}
                  iconPosition="right"
                  onClick={moveNext}
                />
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    </>
  );
};
